import getSymbolFromCurrency from 'currency-symbol-map';
import { IntlShape } from 'react-intl';
import messages from '../../messages';
import productPageMessages from '../../components/Banks/ProductPage/messages';

import { config as offersTableConfig } from '../offersTableConfigs/config';
import {
  getCountrySpecificConfig,
  buildAsyncRegistrationLink,
  buildOpeningOrderFlowLink,
} from '../offersTableConfigs';
import {
  isNotice,
  isOvernight,
  mapBipsToObsDeposits,
  PRODUCT_TYPES,
} from '../BIPSMappingToOBS/deposits';
import { DBSProduct, OFFER_PRODUCT_TYPES } from '../../types';
import { BankDetailsType } from 'types/bank';
import { getMarketingProductType } from '../getMarketingProductType';
import { formatCurrencyPosition, getHighestRateByAvailableProducts } from './index';
import { INTL_LOCALES } from '@raisin/cp-utils';
import { isCallalikeSubtype } from '../helpers';

export const getDefaultRate = (offer: DBSProduct, locale: string) => {
  const countrySpecificConfig = getCountrySpecificConfig(offersTableConfig, locale);

  if (offer?.interest_rates?.headline_interest_rate) {
    const rate =
      offer?.interest_rates[
        `interest_rate_${offer?.interest_rates?.headline_interest_rate.toLowerCase()}`
      ];

    if (rate) return rate;
  }

  return countrySpecificConfig.defaultRate === 'effective_interest_rate'
    ? offer?.interest_rates?.interest_rate_effective
    : offer?.interest_rates?.interest_rate_nominal;
};

export const formatDefaultRate = (offer, locale: string, formatNumber, formatMessage) => {
  const localesWithSpaceAroundRateAndInterest = [
    INTL_LOCALES.GERMANY,
    INTL_LOCALES.AUSTRIA,
    INTL_LOCALES.FRANCE,
    INTL_LOCALES.POLAND,
    INTL_LOCALES.SPAIN,
  ] as string[];

  const rate = getDefaultRate(offer, locale);

  let interestRateAndAmount = formatNumber(rate, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  interestRateAndAmount += localesWithSpaceAroundRateAndInterest.includes(locale) ? ' %' : '%';

  return `${interestRateAndAmount} ${formatMessage(messages.perYear)}`;
};

export const sortBankOffers = (offers: DBSProduct[]) => {
  const PRODUCT_TYPE_ORDER = [
    OFFER_PRODUCT_TYPES.OVERNIGHT,
    OFFER_PRODUCT_TYPES.NOTICE,
    OFFER_PRODUCT_TYPES.TERM,
  ] as string[];

  return [...offers].sort((a, b) => {
    const orderByType =
      PRODUCT_TYPE_ORDER.indexOf(getMarketingProductType(a)) -
      PRODUCT_TYPE_ORDER.indexOf(getMarketingProductType(b));

    const orderByTerm = (a?.term?.period ?? 0) >= (b?.term?.period ?? 0) ? 1 : -1;

    return orderByType || orderByTerm;
  });
};

export const formatCurrency = (offer) =>
  `${getSymbolFromCurrency(offer.currency)} (${offer.currency})`;

export const isNoticeAccountOffer = (offer, showSpecialNoticeAccountAsOvernight) => {
  // Notice Account Offers whose notice period are max 4 days are considered Overnight Offers.
  const MAX_NOTICE_PERIOD_DAYS_FAKE_OVERNIGHT = 4;

  if (showSpecialNoticeAccountAsOvernight) {
    return (
      offer.is_overnight &&
      offer.is_notice_account &&
      offer.payout_period_after_termination > MAX_NOTICE_PERIOD_DAYS_FAKE_OVERNIGHT
    );
  }

  return offer.is_overnight && offer.is_notice_account;
};

export const formatPeriod = (
  termPeriod,
  dbsTerm,
  formatMessage,
  renderWeeks = false,
  locale = '',
) => {
  const yearLabel = termPeriod / 12 === 1 ? 'year' : 'years';
  const years = termPeriod / 12;

  // handling custom key for years >= 5 for PL language
  if (years >= 5 && locale === 'pl-PL') {
    return `${years} ${formatMessage(messages.yearsPL)}`;
  }

  const monthsLabel = termPeriod === 1 ? 'month' : 'months';
  let term = '';
  if (termPeriod >= 12) {
    const formattedYears = Number.isInteger(years) ? years : years.toFixed(1);
    term = `${formattedYears} ${formatMessage(messages[yearLabel])}`;
  } else {
    term = `${termPeriod} ${formatMessage(messages[monthsLabel])}`;
  }

  if (
    renderWeeks &&
    Boolean(dbsTerm?.unit) &&
    Boolean(dbsTerm?.period) &&
    dbsTerm?.unit === 'weeks'
  ) {
    term += ` (${formatMessage(messages.actualTermInWeeks, {
      period: dbsTerm.period,
    })})`;
  }
  return term;
};

const getTermValue = (parameters) => {
  const {
    product,
    termPeriod,
    dbsTerm,
    isProductOvernight,
    isEasyAccessAccount,
    isProductNotice,
    isOfferSubtypeCallalike,
    daysAmount,
    config,
    formatMessage,
    renderInOfferOverview,
    renderWeeks,
    locale,
  } = parameters;

  if (isEasyAccessAccount) {
    return formatMessage(messages.easyAccess);
  }

  if (isProductOvernight) {
    if (isNoticeAccountOffer(product, config.features?.showSpecialNoticeAccountAsOvernight)) {
      return `${daysAmount} ${formatMessage(messages.terminationNoticePeriod)}`;
    }

    if (isOfferSubtypeCallalike) {
      if (renderInOfferOverview) {
        return formatMessage(messages.offerOverviewCallalikeAvailability);
      } else {
        return formatMessage(messages.offerDetailsCallalikeTermValue);
      }
    }

    return formatMessage(messages.productTypeOvernight);
  }

  if (isProductNotice) {
    if (isOfferSubtypeCallalike) {
      return formatMessage(messages.offerOverviewCallalikeAvailability);
    }

    return `${daysAmount} ${formatMessage(messages.terminationNoticePeriod)}`;
  }

  return formatPeriod(termPeriod, dbsTerm, formatMessage, renderWeeks, locale);
};

export const formatTerm = (
  bipsOffer,
  intl,
  formatMessage,
  { renderInOfferOverview = false, renderWeeks = false } = {},
) => {
  const { formatNumber, formatDate, locale } = intl;
  const config = getCountrySpecificConfig(offersTableConfig, locale);
  const offer = mapBipsToObsDeposits(bipsOffer, formatNumber, formatMessage, formatDate, locale);
  const daysAmount = offer.payout_period_after_termination;
  const isOfferSubtypeCallalike = isCallalikeSubtype(
    getMarketingProductType(offer),
    offer.daily_settlement,
  );
  const isEasyAccessAccount =
    config?.showEasyAccessLabel && offer.is_overnight && !offer.is_notice_account;

  const parameters = {
    product: offer,
    termPeriod: offer.number_of_terms,
    dbsTerm: offer.dbs_term,
    isProductOvernight: offer.is_overnight,
    isEasyAccessAccount,
    isProductNotice: offer.is_notice_account,
    isOfferSubtypeCallalike,
    daysAmount,
    formatMessage,
    config,
    renderInOfferOverview,
    renderWeeks,
    locale,
  };

  return getTermValue(parameters);
};

export const formatPromotedProductTerm = (
  promotedProduct,
  intl,
  formatMessage,
  renderInOfferOverview = false,
) => {
  const marketedProductType = getMarketingProductType(promotedProduct);
  const { locale } = intl;
  const config = getCountrySpecificConfig(offersTableConfig, locale);
  const isProductOvernight = isOvernight(marketedProductType);
  const isProductNotice = isNotice(marketedProductType);
  const daysAmount = isProductNotice
    ? promotedProduct.conditions?.termination?.notice_period?.period
    : promotedProduct.conditions?.termination?.notice_period;
  const isOfferSubtypeCallalike =
    isProductOvernight && promotedProduct.conditions?.daily_settlement === false;
  const isEasyAccessAccount = config?.showEasyAccessLabel && isProductOvernight && !isProductNotice;

  const parameters = {
    product: promotedProduct,
    termPeriod: promotedProduct.term?.period,
    isProductOvernight,
    isEasyAccessAccount,
    isProductNotice,
    isOfferSubtypeCallalike,
    daysAmount,
    config,
    formatMessage,
    renderInOfferOverview,
  };

  return getTermValue(parameters);
};

export const getCtaLink = async (locale: string, offer: DBSProduct, isLoggedIn: boolean) => {
  const REGISTRATION_LINK_TYPE = 'raisin_retail';
  if (isLoggedIn) {
    return buildOpeningOrderFlowLink(locale, offer);
  }

  return buildAsyncRegistrationLink(locale, REGISTRATION_LINK_TYPE, offer);
};

export const getCompundingPeriodText = (frequency) => {
  const intervalsByNumberOfMonths = {
    1: 'monthly',
    3: 'quarterly',
    12: 'yearly',
  };

  if (frequency?.unit === 'weekly') {
    return 'weekly';
  }

  return intervalsByNumberOfMonths[frequency?.period];
};

export const getAffiliateBankProductUSPListItems = (
  bank: BankDetailsType,
  bankOffers: DBSProduct[],
  selectedOffer: DBSProduct,
  intl: IntlShape,
  formateMessage,
): string[] => {
  if (!bankOffers || !selectedOffer) {
    return [];
  }

  const listItems: Array<{ order: number; ranking: number; text: string }> = [];

  const displayLabels = selectedOffer?.display?.labels;
  const offerConditions = selectedOffer?.conditions;
  const offerAnnuity = selectedOffer?.conditions?.annuity;

  const compoundingInterestsPeriodLabels = {
    weekly: formateMessage(
      productPageMessages.affiliateHeroBannerListItemCompoundingInterestWeekly,
    ),
    monthly: formateMessage(
      productPageMessages.affiliateHeroBannerListItemCompoundingInterestMonthly,
    ),
    quarterly: formateMessage(
      productPageMessages.affiliateHeroBannerListItemCompoundingInterestQuarterly,
    ),
    yearly: formateMessage(
      productPageMessages.affiliateHeroBannerListItemCompoundingInterestYearly,
    ),
    undefined: '',
  };

  const uspLabels = {
    finanztestBestenListen: {
      ranking: 2,
      order: 1,
      text: formateMessage(productPageMessages.affiliateHeroBannerListItemFinanztestBestenliste),
    },
    saveAlreadyFrom: {
      ranking: 3,
      order: 1,
      text: formateMessage(productPageMessages.affiliateHeroBannerListItemSaveAlreadyFrom, {
        minimum_amount: formatCurrencyPosition(
          intl.formatNumber(Number(offerConditions.opening.minimum_amount), {
            style: 'currency',
            currency: bank?.conditions?.currency,
            maximumFractionDigits: 0,
          }),
          intl.locale,
        ),
      }),
    },
    compoundInterest: {
      ranking: 4,
      order: 1,
      text: formateMessage(productPageMessages.affiliateHeroBannerListItemCompoundingInterest, {
        period:
          compoundingInterestsPeriodLabels[
            getCompundingPeriodText(selectedOffer?.conditions?.annuity?.frequency)
          ],
      }),
    },
    interestRatePaidOut: {
      ranking: 5,
      order: 1,
      text: formateMessage(productPageMessages.affiliateHeroBannerListItemInterestRatePaidOut, {
        frequency:
          compoundingInterestsPeriodLabels[
            getCompundingPeriodText(selectedOffer?.conditions?.annuity?.frequency)
          ],
      }),
    },
    savingsPlanPossible: {
      ranking: 6,
      order: 1,
      text: formateMessage(productPageMessages.affiliateHeroBannerListItemSavingsPlanPossible),
    },
    enjoyHighRates: {
      ranking: 7,
      order: 1,
      text: formateMessage(productPageMessages.affiliateHeroBannerListItemEnjoyFixesAndHighRates, {
        highest_rate: `${intl.formatNumber(
          Number(getHighestRateByAvailableProducts(bankOffers, intl.locale)) || 0,
          {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          },
        )}%`,
      }),
    },
    noCosts: {
      ranking: 1,
      order: 2,
      text: formateMessage(productPageMessages.affiliateHeroBannerListItemNoCosts),
    },
  };

  if (displayLabels?.includes('finanztest_bestenliste')) {
    listItems.push(uspLabels.finanztestBestenListen);
  }

  if (
    offerConditions?.opening?.minimum_amount &&
    Number(offerConditions?.opening?.minimum_amount) <= 5000
  ) {
    listItems.push(uspLabels.saveAlreadyFrom);
  }

  if (offerAnnuity?.compound_interest) {
    listItems.push(uspLabels.compoundInterest);
  }

  if (
    offerAnnuity?.annuity_payout &&
    offerAnnuity?.frequency?.period &&
    offerAnnuity?.frequency?.period <= 12 &&
    offerAnnuity?.frequency?.unit === 'months'
  ) {
    listItems.push(uspLabels.interestRatePaidOut);
  }

  if (offerConditions?.top_up?.enabled) {
    listItems.push(uspLabels.savingsPlanPossible);
  }

  listItems.push(uspLabels.enjoyHighRates);
  listItems.push(uspLabels.noCosts);

  // sort by ranking first
  let orderedListItems = [...listItems].sort((a, b) => a.ranking - b.ranking);

  if (orderedListItems.length > 3) {
    orderedListItems = orderedListItems.splice(0, 3);
  }
  // sort by order as a second condition
  return [...orderedListItems].sort((a, b) => a.order - b.order).map((item) => item.text);
};

const DEPOSIT_GUARANTEE_LINKS = {
  GBR: 'https://www.fscs.org.uk/',
  SWE: 'https://riksgalden.se/en/',
  MLT: 'https://mfsa.mt/consumers/consumer-awareness-and-education/banking/accounts/depositor-compensation-scheme/',
  GIB: 'https://gdgb.gi/',
};

export const getDepositGuaranteeLink = (countryCode) => DEPOSIT_GUARANTEE_LINKS[countryCode];

export const formatTermForBannerHero = (selectedOffer, bank, intl, formatMessage) => {
  if (selectedOffer?.product_type === PRODUCT_TYPES.NOTICE) {
    return formatMessage(productPageMessages.affiliateHeroBannerTitleForNa, {
      interest_rate: formatDefaultRate(
        selectedOffer,
        intl.locale,
        intl.formatNumber,
        formatMessage,
      ),
      bank_name: bank.name,
    });
  }

  if (selectedOffer?.product_type === PRODUCT_TYPES.OVERNIGHT) {
    return formatMessage(productPageMessages.affiliateHeroBannerTitleForOn, {
      interest_rate: formatDefaultRate(
        selectedOffer,
        intl.locale,
        intl.formatNumber,
        formatMessage,
      ),
      bank_name: bank.name,
    });
  }

  return formatMessage(productPageMessages.affiliateHeroBannerTitle, {
    interest_rate: formatDefaultRate(selectedOffer, intl.locale, intl.formatNumber, formatMessage),
    term: formatTerm(selectedOffer, intl, formatMessage),
    bank_name: bank.name,
  });
};
